import z, { treeifyError } from "zod";

type FieldError =
  | string
  | (FieldError | undefined)[]
  | {
      [key: string]: FieldError | undefined;
    };

function extractErrors(
  tree: z.core.$ZodErrorTree<any>
): FieldError | undefined {
  if ("properties" in tree && tree.properties) {
    const entries = Object.entries(tree.properties);
    return Object.fromEntries(
      entries.map(([key, value]) => [key, value && extractErrors(value)])
    );
  }

  if ("items" in tree && tree.items) {
    if (Array.isArray(tree.items)) {
      return tree.items.map((value) => extractErrors(value));
    }
    const entries = Object.entries(tree.items);
    return Object.fromEntries(
      entries.map(([key, value]) => [
        key.toString(),
        value && extractErrors(value),
      ])
    );
  }

  return tree.errors[0];
}

export const validateWithZod =
  <T extends object>(schema: z.ZodSchema<T>) =>
  (values: unknown): Record<string, FieldError> => {
    const errors: Record<string, FieldError> = {};
    const parseResult = schema.safeParse(values);
    if (parseResult.error) {
      const errorsTree = treeifyError<T>(parseResult.error);
      const extracted = extractErrors(errorsTree);

      if (typeof extracted == "string") {
        errors.global = extracted;
      } else {
        Object.assign(errors, extracted);
      }
    }

    return errors;
  };

export type InferSchema<T extends z.ZodSchema> = z.infer<T>;
